import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Pick one teammate for a direct message, or a few for a group. Submitting
 * with exactly one person picked always resolves to the same DM on the
 * backend — see create_conversation's dedupe — so re-opening this and
 * picking the same person again just returns to the existing thread.
 */
export default function NewConversationDialog({ open, onOpenChange, onCreated }) {
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setSelected([]);
    setTitle("");
    setLoading(true);
    api
      .get("/messaging/colleagues")
      .then(({ data }) => setColleagues(data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return colleagues;
    return colleagues.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(term) ||
        (c.email || "").toLowerCase().includes(term)
    );
  }, [colleagues, q]);

  const toggle = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const isGroup = selected.length > 1;

  const submit = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one person");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", {
        participant_ids: selected,
        title: isGroup ? title.trim() : undefined,
      });
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New message</DialogTitle>
          <DialogDescription>
            Pick one person for a direct message, or a few for a group.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="relative">
            <Search className="w-4 h-4 text-muted2 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your team…"
              className="pl-9"
              autoFocus
            />
          </div>

          {isGroup && (
            <div className="space-y-1.5">
              <Label htmlFor="group-title">Group name (optional)</Label>
              <Input
                id="group-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={selected
                  .map((id) => colleagues.find((c) => c.user_id === id)?.name)
                  .filter(Boolean)
                  .join(", ")}
                maxLength={120}
              />
            </div>
          )}

          <div className="border border-line rounded-xl max-h-[280px] overflow-y-auto">
            {loading ? (
              <div className="py-8 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-brand" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-[12.5px] text-muted2 px-4">
                {colleagues.length === 0
                  ? "No one else on the team yet — add teammates from Team first."
                  : "No matches"}
              </div>
            ) : (
              filtered.map((c) => {
                const isSel = selected.includes(c.user_id);
                return (
                  <button
                    key={c.user_id}
                    type="button"
                    onClick={() => toggle(c.user_id)}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-3 py-2.5 text-left border-b border-line last:border-b-0 hover:bg-brand-light/40 transition-colors",
                      isSel && "bg-brand-light/60"
                    )}
                  >
                    <div className="relative shrink-0">
                      <div className="w-8 h-8 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[12px]">
                        {(c.name?.[0] || "?").toUpperCase()}
                      </div>
                      {c.online && (
                        <span
                          className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card"
                          title="Online"
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-ink truncate">{c.name}</div>
                      <div className="text-[11.5px] text-muted2 truncate">{c.email}</div>
                    </div>
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full border flex items-center justify-center shrink-0",
                        isSel ? "bg-brand border-brand" : "border-line"
                      )}
                    >
                      {isSel && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || selected.length === 0}
            className="bg-brand hover:bg-brand-hover"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isGroup ? (
              "Create group"
            ) : (
              "Start conversation"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
